import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Music, Video, Volume2, Mic2, Layers3, Zap,
  SlidersHorizontal, Mic as MicIcon, MicVocal, ArrowUpRight, Megaphone, Podcast,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useT } from "@/lib/i18n";

const STUDIOS = [
  { to: "/dashboard/generate", label: "AI Studio", icon: Sparkles, tint: "text-brand-amber bg-brand-amber/10 border-brand-amber/30",
    desc: "Your everyday generator — scripts, captions, bios and station copy in seconds." },
  { to: "/dashboard/music-studio", label: "AI Music Studio", icon: Music, tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
    desc: "Produce station-branded music beds and full tracks with AI." },
  { to: "/dashboard/vision-studio", label: "Vision Studio", icon: Video, badge: "PRO", tint: "text-violet-300 bg-violet-500/10 border-violet-500/30",
    desc: "Cinematic AI video — promos, spots and social clips." },
  { to: "/dashboard/audio-studio", label: "Audio Studio", icon: Volume2, tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    desc: "Record, edit and polish audio right in the browser." },
  { to: "/dashboard/avatar-studio", label: "AI Avatar Studio", icon: Mic2, tint: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    desc: "Talking-head AI presenters for news, weather and promos." },
  { to: "/dashboard/imaging", label: "Imaging Studio", icon: Volume2, tint: "text-orange-300 bg-orange-500/10 border-orange-500/30",
    desc: "Sweepers, station IDs and drops that glue your sound together." },
  { to: "/dashboard/stem-studio", label: "Stem Studio", icon: Layers3, tint: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    desc: "Split any track into clean, isolated stems — karaoke cuts to full producer kits." },
  { to: "/dashboard/vocalist-studio", label: "AI Vocalist Studio", icon: MicIcon, tint: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/30",
    desc: "AI singing vocals for jingles, hooks and station songs." },
  { to: "/dashboard/karaoke-studio", label: "Karaoke Studio", icon: MicVocal, tint: "text-pink-300 bg-pink-500/10 border-pink-500/30",
    desc: "Instant karaoke versions of any track — lyrics and all." },
  { to: "/dashboard/karaoke-prompter", label: "Karaoke Prompter", icon: MicVocal, tint: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    desc: "Word-by-word lyric sync for on-air karaoke — classic bouncing-highlight style." },
  { to: "/dashboard/artist-drops", label: "Voice Drops", icon: MicIcon, tint: "text-lime-300 bg-lime-500/10 border-lime-500/30",
    desc: "Artist and DJ voice drops — quick branded shoutouts for your airwaves." },
  { to: "/dashboard/quick-promo-creator", label: "Quick Promo Creator", icon: Zap, tint: "text-yellow-300 bg-yellow-500/10 border-yellow-500/30",
    desc: "A ready-to-air promo in under a minute — script to mixdown." },
  { to: "/dashboard/commercials", label: "Commercials", icon: Megaphone, tint: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    desc: "AI-written and voiced commercials, ready for your sponsors." },
  { to: "/dashboard/production-suite", label: "Production Suite", icon: SlidersHorizontal, tint: "text-teal-300 bg-teal-500/10 border-teal-500/30",
    desc: "The full DAW — multitrack timeline for complete productions." },
  { to: "/dashboard/podcasts", label: "Podcast Studio", icon: Podcast, tint: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30",
    desc: "Record, produce and publish your station's podcasts — Vault and Creator inside." },
];

export default function StudioCity() {
  const t = useT();
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="studio-city-page">
      <PageHeader
        eyebrow="// STUDIO CITY"
        title="Welcome to Studio City."
        description="Fifteen production rooms. One creative district. Pick a door."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {STUDIOS.map((s, i) => (
          <Link
            key={s.to}
            to={s.to}
            data-testid={`studio-card-${s.to.split("/").pop()}`}
            className="group relative rounded-xl border border-line bg-bg-surface p-5 transition hover:border-brand-amber/60 hover:bg-bg-elevated"
            style={{ animation: `fade-up 0.4s ease both`, animationDelay: `${i * 45}ms` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`h-11 w-11 rounded-lg border flex items-center justify-center ${s.tint}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-ink-muted opacity-0 group-hover:opacity-100 group-hover:text-brand-amber transition" />
            </div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="font-display text-lg font-bold">{t(s.label)}</div>
              {s.badge && (
                <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-amber/15 text-brand-amber">
                  {s.badge}
                </span>
              )}
            </div>
            <div className="text-sm text-ink-secondary leading-relaxed">{t(s.desc)}</div>
          </Link>
        ))}
      </div>
      <div className="mt-8 text-[11px] font-mono uppercase tracking-[0.2em] text-ink-muted text-center">
        // every door leads somewhere loud
      </div>
    </div>
  );
}
